import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Stale-while-revalidate fetch for one analytics endpoint.
 *
 * status: 'loading'    first ever load, nothing to show yet (skeleton)
 *         'refreshing' a newer request is running; `data` keeps the last good value
 *         'ready'      data is current
 *         'error'      the last request failed; `data` is whatever was loaded before (or null)
 *         'idle'       disabled
 *
 * A new params key (or refetch) aborts the in-flight request, and a response that is no longer
 * the latest is dropped. `resetKey` marks data that must never be shown for another scope (a
 * marketing manager's space): changing it discards the old data instead of keeping it stale.
 */
export function useAnalyticsResource(fetcher, params, { enabled = true, resetKey = '' } = {}) {
  const [state, setState] = useState({ data: null, error: null, status: 'loading', scope: resetKey });
  const [reloadTick, setReloadTick] = useState(0);
  const latestRequest = useRef(0);
  const fetcherRef = useRef(fetcher);
  const paramsKey = JSON.stringify(params);

  useEffect(() => { fetcherRef.current = fetcher; });

  useEffect(() => {
    if (!enabled) return undefined;

    latestRequest.current += 1;
    const requestId = latestRequest.current;
    const controller = new AbortController();

    setState((prev) => {
      const keep = prev.scope === resetKey && prev.data !== null;
      return { data: keep ? prev.data : null, error: null, status: keep ? 'refreshing' : 'loading', scope: resetKey };
    });

    fetcherRef.current(JSON.parse(paramsKey), { signal: controller.signal })
      .then((data) => {
        if (requestId !== latestRequest.current) return;
        setState({ data, error: null, status: 'ready', scope: resetKey });
      })
      .catch((error) => {
        if (requestId !== latestRequest.current || controller.signal.aborted) return;
        setState((prev) => ({ data: prev.scope === resetKey ? prev.data : null, error, status: 'error', scope: resetKey }));
      });

    return () => {
      controller.abort();
      latestRequest.current += 1;
    };
  }, [paramsKey, enabled, resetKey, reloadTick]);

  const refetch = useCallback(() => setReloadTick((t) => t + 1), []);

  const current = state.scope === resetKey;
  return {
    data: current ? state.data : null,
    error: current ? state.error : null,
    status: !enabled ? 'idle' : current ? state.status : 'loading',
    refetch,
  };
}
